import { trackInternalEvent } from '@rio/tracking'
import { useCallback } from 'react'
import { Optional } from '@rio/types'
import { useI18n } from 'builder/hooks/useI18n'
import { Icon24 } from 'builder/components/Icon'
import { SPACING } from 'builder/modules/constants'
import { SpacingChangeHandler } from 'builder/modules/resumeEditor'
import { SpacingContainer, SpacingIcon, SpacingButton, Tooltip, TooltipValue } from './styles'

type Props = {
  value: Optional<number>
  onChange: SpacingChangeHandler
  isSupported?: boolean
  isTopbarLayout?: boolean
}

type OperationType = 'increase' | 'decrease'

// Protects the value from going out of bounds [-1, 1]
// Haven't seen this kind of error, but anyway better to do that
const normalize = (value: number) => Math.min(Math.max(value, SPACING.min), SPACING.max)

// Converts the value to a user-friendly format [50%, 150%]
const prettify = (value: number) => `${100 + Math.round(50 * value)}%`

export const SpacingControl = (props: Props) => {
  const { isSupported = false, onChange, isTopbarLayout } = props
  const value = props.value ?? 0
  const { i18n } = useI18n()
  const FontSizeIcon = isSupported ? Icon24.FontSize : Icon24.FontSizeLock

  const changeSpacing = useCallback(
    (operation: OperationType) => {
      const oldValue = value
      const newValue = operation === 'decrease' ? value - SPACING.step : value + SPACING.step
      trackInternalEvent('change_line_spacing', {
        label: isTopbarLayout ? 'builder' : 'templates',
        direction: operation,
        old_value: oldValue,
        new_value: newValue,
      })
      onChange(normalize(newValue), oldValue)
    },
    [isTopbarLayout, onChange, value],
  )

  const onButtonClick = useCallback(
    (operation: OperationType) => {
      changeSpacing(operation)
    },
    [changeSpacing],
  )

  return (
    <SpacingContainer>
      <SpacingButton
        isDisabled={!isSupported || value === SPACING.min}
        onClick={() => onButtonClick('decrease')}
        isTopbarLayout={isTopbarLayout}
      >
        <Icon24.Remove />
      </SpacingButton>

      <SpacingIcon>
        <FontSizeIcon />
      </SpacingIcon>

      <SpacingButton
        isDisabled={!isSupported || value === SPACING.max}
        onClick={() => onButtonClick('increase')}
        isTopbarLayout={isTopbarLayout}
      >
        <Icon24.Add />
      </SpacingButton>

      {isSupported && (
        <Tooltip small>
          {i18n.t('builder.resume_editor.spacing')}
          <TooltipValue>{prettify(value)}</TooltipValue>
        </Tooltip>
      )}

      {!isSupported && <Tooltip>{i18n.t('builder.resume_editor.no_spacing')}</Tooltip>}
    </SpacingContainer>
  )
}
